import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IJobsActionsState } from 'Components/JobsTs/JobsModel';
import { AxiosResponse } from 'axios';
import httpAdapterInstance from 'configs/HttpAdapterConfig';
import { IBaseResponse } from 'shared/SharedModels';
import { DefaultAPIErrorMsg, ENDPOINTS } from 'shared/constants';

const initialJobsActionsState: IJobsActionsState = {};

export const deleteJobById = createAsyncThunk<IBaseResponse, { jobId: number }, { rejectValue: IBaseResponse }>(
    "deleteJobById",
    async ({ jobId }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .delete(`${ENDPOINTS.JOB}/${jobId}`)
            .then((response: AxiosResponse<IBaseResponse>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

export const stopHireById = createAsyncThunk<IBaseResponse, { jobId: number }, { rejectValue: IBaseResponse }>(
    "stopHireById",
    async ({ jobId }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .put(`${ENDPOINTS.STOP_HIRING}/${jobId}/stopHiring`)
            .then((response: AxiosResponse<IBaseResponse>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

export const rePostJob = createAsyncThunk<(IBaseResponse & { id: number }), { jobId: string }, { rejectValue: IBaseResponse }>(
    "rePostJob",
    async ({ jobId }, { rejectWithValue }) => {
        return await httpAdapterInstance
            .put(`${ENDPOINTS.JOBS}/${jobId}/copy`)
            .then((response: AxiosResponse<(IBaseResponse & { id: number })>) => response?.data)
            .catch((error) => {
                throw rejectWithValue(error.response.data);
            });
    }
);

const jobsListActionsSlice = createSlice({
    name: 'jobsListActions',
    initialState: initialJobsActionsState,
    reducers: {
        resetJobDelete: (state) => { state.deleteJobResponse = ''; state.deleteJobStatus = 'idle' },
        resetStopHire: (state) => { state.stopHireJobResponse = ''; state.stopHireJobStatus = 'idle' },
        resetRePostJob: (state) => {
            state.rePostJobResponse = '';
            state.rePostJobStatus = 'idle';
            state.copiedJobId = undefined;
            state.prevJobId = undefined;
        }
    },
    extraReducers: (builder) => {
        // delete job
        builder.addCase(deleteJobById.pending, (state) => {
            state.deleteJobStatus = 'pending'
        });
        builder.addCase(deleteJobById.fulfilled, (state, action) => {
            state.deleteJobStatus = 'success';
            state.deleteJobResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });
        builder.addCase(deleteJobById.rejected, (state, action) => {
            state.deleteJobStatus = 'failed';
            state.stopHireJobResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });
        // stop hiring for a job
        builder.addCase(stopHireById.pending, (state) => {
            state.stopHireJobStatus = 'pending'
        });
        builder.addCase(stopHireById.fulfilled, (state, action) => {
            state.stopHireJobStatus = 'success';
            state.stopHireJobResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });
        builder.addCase(stopHireById.rejected, (state, action) => {
            state.stopHireJobStatus = 'failed';
            state.stopHireJobResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });
        // re-post a job
        builder.addCase(rePostJob.pending, (state) => {
            state.rePostJobStatus = 'pending'
        });
        builder.addCase(rePostJob.fulfilled, (state, action) => {
            state.rePostJobStatus = 'success';
            state.copiedJobId = action.payload.id;
            state.prevJobId = parseInt(action.meta.arg.jobId);
            state.rePostJobResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });
        builder.addCase(rePostJob.rejected, (state, action) => {
            state.rePostJobStatus = 'failed';
            state.rePostJobResponse = action?.payload?.message ?? DefaultAPIErrorMsg;
        });
    }
});

export const { resetJobDelete, resetStopHire, resetRePostJob } = jobsListActionsSlice.actions;
export default jobsListActionsSlice;
